import { useState } from 'react';
import {
  Alert, Badge, Button, Card, Divider, Grid, Group, JsonInput, NumberInput,
  Radio, Select, Stack, Switch, Text, TextInput, Textarea,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconSend, IconTestPipe } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

import { useSegments, useSendMessage, useTemplates } from '../api/hooks';
import { PageHeader } from '../components/PageHeader';
import { ApiRequestError } from '../api/client';
import type { Channel } from '../api/types';

export function ComposePage() {
  const navigate = useNavigate();
  const send = useSendMessage();
  const [channel, setChannel] = useState<Channel>('push');
  const { data: templates } = useTemplates(channel);
  const { data: segments } = useSegments();
  const [error, setError] = useState<string | null>(null);

  const form = useForm({
    initialValues: {
      audienceType: 'segment' as 'segment' | 'all' | 'profile',
      segmentId: '',
      externalId: '',
      templateId: '',
      category: 'marketing',
      priority: 5,
      dryRun: true,
      title: '',
      body: '',
      data: '{}',
    },
  });

  const segment = segments?.find((s) => s.id === form.values.segmentId);
  const usesTemplate = !!form.values.templateId;
  // Push is the only channel whose content can be written inline; the others need
  // a template, because their content has structure an ad-hoc payload cannot
  // express (a subject line, a registered DLT body).
  const needsTemplate = channel !== 'push';

  function submit(values: typeof form.values) {
    setError(null);

    const audience =
      values.audienceType === 'segment'
        ? { type: 'segment', segment_id: values.segmentId }
        : values.audienceType === 'profile'
          ? { type: 'profile', external_id: values.externalId }
          : { type: 'all' };

    let data: Record<string, unknown> = {};
    try {
      data = values.data.trim() ? JSON.parse(values.data) : {};
    } catch {
      setError('Template data is not valid JSON.');
      return;
    }

    send.mutate(
      {
        channel,
        category: values.category,
        priority: values.priority,
        dry_run: values.dryRun,
        audience,
        ...(values.templateId ? { template_id: values.templateId } : {}),
        ...(channel === 'push' && !values.templateId
          ? { push: { title: values.title, body: values.body } }
          : {}),
        data,
      },
      {
        onSuccess: (message) => {
          notifications.show({
            color: values.dryRun ? 'blue' : 'teal',
            title: values.dryRun ? 'Dry run started' : 'Send accepted',
            message: values.dryRun
              ? 'Every gate ran and the content rendered. Nothing was dispatched.'
              : 'Fan-out is happening now.',
          });
          navigate(`/messages/${message.id}`);
        },
        onError: (e) => {
          setError(
            e instanceof ApiRequestError
              ? [e.message, ...Object.values(e.fieldErrors())].join(' — ')
              : 'Send failed',
          );
        },
      },
    );
  }

  return (
    <>
      <PageHeader
        title="Send"
        description="Compose a one-off send. Recurring or conditional sends belong in a journey."
      />

      <form onSubmit={form.onSubmit(submit)}>
        <Grid gutter="lg">
          <Grid.Col span={{ base: 12, md: 7 }}>
            <Stack>
              <Card>
                <Text fw={500} mb="md">Channel</Text>
                <Radio.Group
                  value={channel}
                  onChange={(v) => {
                    setChannel(v as Channel);
                    form.setFieldValue('templateId', '');
                  }}
                >
                  <Group>
                    <Radio value="push" label="Push" />
                    <Radio value="email" label="Email" />
                    <Radio value="sms" label="SMS" />
                    <Radio value="whatsapp" label="WhatsApp" />
                  </Group>
                </Radio.Group>
              </Card>

              <Card>
                <Text fw={500} mb="md">Audience</Text>
                <Stack>
                  <Radio.Group {...form.getInputProps('audienceType')}>
                    <Stack gap="xs">
                      <Radio value="segment" label="A segment" />
                      <Radio value="profile" label="One profile, by external ID" />
                      <Radio
                        value="all"
                        label="Everyone reachable on this channel"
                      />
                    </Stack>
                  </Radio.Group>

                  {form.values.audienceType === 'segment' && (
                    <Select
                      label="Segment"
                      placeholder="Choose a segment"
                      searchable
                      data={(segments ?? []).map((s) => ({ value: s.id, label: s.name }))}
                      {...form.getInputProps('segmentId')}
                      description={segment?.summary}
                    />
                  )}

                  {form.values.audienceType === 'profile' && (
                    <TextInput
                      label="External ID"
                      placeholder="user-1"
                      {...form.getInputProps('externalId')}
                    />
                  )}

                  {form.values.audienceType === 'all' && (
                    <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />}>
                      This targets every profile with a usable address for this channel.
                      Keep the dry run on for the first attempt.
                    </Alert>
                  )}
                </Stack>
              </Card>

              <Card>
                <Text fw={500} mb="md">Content</Text>
                <Stack>
                  <Select
                    label="Template"
                    placeholder={needsTemplate ? 'Required for this channel' : 'Optional for push'}
                    clearable={!needsTemplate}
                    searchable
                    data={(templates ?? []).map((t) => ({
                      value: t.id,
                      label: `${t.name}${t.dlt_status === 'approved' ? ' · DLT approved' : ''}`,
                    }))}
                    {...form.getInputProps('templateId')}
                  />

                  {channel === 'push' && !usesTemplate && (
                    <>
                      <TextInput
                        label="Title"
                        placeholder="Order shipped"
                        {...form.getInputProps('title')}
                      />
                      <Textarea
                        label="Body"
                        placeholder="It is on its way. {{ profile.first_name }}, track it in the app."
                        autosize
                        minRows={2}
                        {...form.getInputProps('body')}
                      />
                      <Text size="xs" c="dimmed">
                        Liquid works here too: <code>{'{{ profile.first_name }}'}</code>.
                      </Text>
                    </>
                  )}

                  <JsonInput
                    label="Template data"
                    description="Available to the template as {{ data.… }}"
                    autosize
                    minRows={3}
                    formatOnBlur
                    validationError="Not valid JSON"
                    {...form.getInputProps('data')}
                  />
                </Stack>
              </Card>
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 5 }}>
            <Stack>
              <Card>
                <Text fw={500} mb="md">Delivery</Text>
                <Stack>
                  <Select
                    label="Category"
                    description="Drives the preference gate and the frequency cap"
                    data={[
                      { value: 'marketing', label: 'Marketing — respects opt-outs and caps' },
                      { value: 'product', label: 'Product update' },
                      { value: 'transactional', label: 'Transactional — bypasses quiet hours' },
                      { value: 'billing', label: 'Billing — bypasses quiet hours' },
                      { value: 'security', label: 'Security — bypasses quiet hours and caps' },
                    ]}
                    {...form.getInputProps('category')}
                  />

                  <NumberInput
                    label="Priority"
                    description="9 jumps the queue, 1 is bulk. A password reset should never queue behind a broadcast."
                    min={0}
                    max={10}
                    {...form.getInputProps('priority')}
                  />

                  <Divider />

                  <Switch
                    label="Dry run"
                    description="Runs every gate and renders the content for each recipient, then stops without dispatching. The delivery list shows exactly who WOULD have received it."
                    checked={form.values.dryRun}
                    onChange={(e) => form.setFieldValue('dryRun', e.currentTarget.checked)}
                  />
                </Stack>
              </Card>

              {error && (
                <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
                  {error}
                </Alert>
              )}

              <Button
                type="submit"
                size="md"
                loading={send.isPending}
                color={form.values.dryRun ? 'blue' : 'brand'}
                leftSection={
                  form.values.dryRun ? <IconTestPipe size={18} /> : <IconSend size={18} />
                }
              >
                {form.values.dryRun ? 'Run dry run' : 'Send for real'}
              </Button>

              {!form.values.dryRun && (
                <Group gap={6} justify="center">
                  <Badge color="red" variant="light">Live</Badge>
                  <Text size="xs" c="dimmed">This will reach real people.</Text>
                </Group>
              )}
            </Stack>
          </Grid.Col>
        </Grid>
      </form>
    </>
  );
}
